import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Users, Trophy, Send, Heart, Lock, Coins, Calendar, Clock, Activity } from 'lucide-react';
import { SiX } from '@icons-pack/react-simple-icons';
import { GitFork } from 'lucide-react';
import PremiumContributionChart from '../ui/PremiumContributionChart';
import { formatNumber, formatTokenAmount, formatRemainingTime } from '../../utils/formatters';

interface OverviewTabProps {
  userData: any;
  timeSeriesData: any;
  githubActivityTypes: any;
  xActivityTypes: any;
  onShowXPosts: () => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ 
  userData, 
  timeSeriesData, 
  githubActivityTypes, 
  xActivityTypes, 
  onShowXPosts 
}) => {
  return (
    <>
      {/* Hero Stats Section - Keep same structure, clean styling */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-[#10131F] rounded-2xl p-8 text-center border border-white/5 shadow-lg shadow-black/20">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <div className="text-sm text-[#7C8694] mb-2 uppercase tracking-wide">Total AmPoints Earned</div>
            <div className="text-5xl md:text-6xl font-bold text-[#00FF9D] mb-4">
              {formatNumber(userData.points)}
            </div>
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#3EBEFF]" />
                <span className="text-[#7C8694]">AI Points: </span>
                <span className="text-[#3EBEFF] font-semibold">{formatNumber(userData.totalAiPoints)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#FFD964]" />
                <span className="text-[#7C8694]">Best Streak: </span>
                <span className="text-[#FFD964] font-semibold">
                  {Math.max(
                    userData.streaks?.gm?.current || 0,
                    userData.streaks?.gn?.current || 0,
                    userData.streaks?.sendit?.current || 0
                  )} days
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* In OverviewTab.tsx, add a dedicated spawn guild card */}
{userData.spawnGuildId && userData.spawnGuildId !== "Not set" && (
  <motion.div 
    className="mb-8"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3 }}
  >
    <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20 backdrop-blur-sm relative overflow-hidden">
      {/* Subtle glow backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#00FF9D]/5 to-transparent pointer-events-none" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-[#00FF9D]/10 backdrop-blur-sm">
              <Users className="w-6 h-6 text-[#00FF9D]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Origin Guild</h3>
              <p className="text-sm text-[#7C8694]">Where your journey began</p>
            </div>
          </div>
          <div className="text-xs text-[#00FF9D] font-mono bg-[#00FF9D]/10 px-3 py-1 rounded-full">
            SPAWN POINT
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-[#7C8694] mb-1">Guild ID</div>
            <div className="font-mono text-white break-all">{userData.spawnGuildId}</div>
          </div>
          {userData.allGuildsData?.[userData.spawnGuildId] && (
            <div className="text-right">
              <div className="text-sm text-[#7C8694] mb-1">Guild Name</div>
              <div className="text-white font-medium">{userData.allGuildsData[userData.spawnGuildId]}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.div>
)}

      {/* Secondary Stats Grid - Keep 4-card layout, clean styling */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 rounded-lg bg-[#3EBEFF]/10">
                  <Cpu className="w-5 h-5 md:w-6 md:h-6 text-[#3EBEFF]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#7C8694]">AI Interactions</h3>
                  <p className="text-2xl font-bold text-white">{formatNumber(userData.totalAiPoints)}</p>
                </div>
              </div>
              <p className="text-sm text-[#7C8694]">Smart activity points</p>
            </div>
          </div>
        </div>

        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 rounded-lg bg-[#00FF9D]/10">
                  <Users className="w-5 h-5 md:w-6 md:h-6 text-[#00FF9D]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#7C8694]">Guild Roles</h3>
                  <p className="text-2xl font-bold text-white">{Object.values(userData.guildRoles || {}).flat().length}</p>
                </div>
              </div>
              <p className="text-sm text-[#7C8694]">Community positions</p>
            </div>
          </div>
        </div>

        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20 cursor-pointer hover:shadow-xl hover:shadow-black/30 transition-all" onClick={onShowXPosts}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 rounded-lg bg-white/10">
                  <SiX className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#7C8694]">X Posts</h3>
                  <p className="text-2xl font-bold text-white">{userData.xPostingsCount || 0}</p>
                </div>
              </div>
              <p className="text-sm text-[#7C8694]">Social engagement</p>
            </div>
          </div>
        </div>

        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 rounded-lg bg-[#9FA6B2]/10">
                  <GitFork className="w-5 h-5 md:w-6 md:h-6 text-[#9FA6B2]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#7C8694]">GitHub Activity</h3>
                  <p className="text-2xl font-bold text-white">{userData.githubInteractionsCount || 0}</p>
                </div>
              </div>
              <p className="text-sm text-[#7C8694]">Code contributions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Charts Section - Clean styling */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <PremiumContributionChart 
            data={githubActivityTypes} 
            type="bar" 
            title="GitHub Activity Distribution"
            color="#9FA6B2"
          />
        </div>

        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <PremiumContributionChart 
            data={xActivityTypes} 
            type="bar" 
            title="X Platform Engagement"
            color="#3EBEFF"
          />
        </div>
      </div>

      {/* Financial Overview Section - Clean styling */}
      <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20 mb-8">
        <h3 className="text-xl font-bold text-white mb-6">Financial Activity</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { 
              icon: Send, 
              label: "Tips Sent", 
              value: userData.totalTipsSent || "0", 
              color: "#00FF9D",
              suffix: " SEND"
            },
            { 
              icon: Heart, 
              label: "Tips Received", 
              value: userData.totalTipsReceived || "0", 
              color: "#3EBEFF",
              suffix: " SEND"
            },
            { 
              icon: Coins, 
              label: "Available Balance", 
              value: formatTokenAmount(userData.availableBalance || 0), 
              color: "#FFD964",
              suffix: " SEND"
            },
            { 
              icon: Lock, 
              label: "Staked Amount", 
              value: formatTokenAmount(userData.staking.amount || 0), 
              color: "#9FA6B2",
              suffix: " SEND"
            }
          ].map((stat, i) => (
            <motion.div
              key={i}
              className="relative p-6 bg-[#0A0E1B]/40 rounded-xl border border-white/5 hover:border-white/10 transition-all duration-300 group"
              whileHover={{ y: -2 }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <stat.icon 
                className="w-8 h-8 mb-4 group-hover:scale-110 transition-transform duration-300" 
                style={{ color: stat.color }} 
              />
              <div className="text-lg font-bold text-white mb-1">
                {stat.value}{stat.suffix}
              </div>
              <div className="text-sm text-[#7C8694]">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Staking Overview - Clean styling */}
      {userData.staking.amount > 0 && (
        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Staking Details</h3>
              <p className="text-sm text-[#7C8694]">Your SEND token staking information</p>
            </div>
            <motion.div 
              className="p-4 rounded-xl bg-gradient-to-br from-[#00FF9D]/15 to-[#3EBEFF]/15 border border-[#00FF9D]/20"
              whileHover={{ scale: 1.05 }}
            >
              <Lock className="w-6 h-6 text-[#00FF9D]" />
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <motion.div 
              className="text-center p-6 bg-[#0A0E1B]/60 rounded-xl border border-white/5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
              <Coins className="w-6 h-6 text-[#00FF9D] mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {formatTokenAmount(userData.staking.amount)}
              </div>
              <div className="text-sm text-[#7C8694]">SEND Staked</div>
            </motion.div>
            
            <motion.div 
              className="text-center p-6 bg-[#0A0E1B]/60 rounded-xl border border-white/5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
              <Calendar className="w-6 h-6 text-[#3EBEFF] mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {userData.staking.lockDurationDays?.toFixed(0) || 0}
              </div>
              <div className="text-sm text-[#7C8694]">Days Locked</div>
            </motion.div>
            
            <motion.div 
              className="text-center p-6 bg-[#0A0E1B]/60 rounded-xl border border-white/5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
              <Clock className="w-6 h-6 text-[#FFD964] mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">
                {formatRemainingTime(userData.staking.remainingSeconds)}
              </div>
              <div className="text-sm text-[#7C8694]">Time Remaining</div>
            </motion.div>
          </div>

          {/* Progress Bar */}
          {userData.staking.lockDurationDays > 0 && (
            <div>
              <div className="flex justify-between text-sm mb-3">
                <span className="text-[#7C8694]">Unlock Progress</span>
                <span className="text-white font-medium">
                  {((1 - userData.staking.remainingDays / userData.staking.lockDurationDays) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="h-3 bg-[#0A0E1B]/60 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#00FF9D] via-[#1AFD9C] to-[#3EBEFF] relative"
                  initial={{ width: 0 }}
                  animate={{ 
                    width: `${(1 - userData.staking.remainingDays / userData.staking.lockDurationDays) * 100}%`
                  }}
                  transition={{ duration: 2, ease: "easeOut" }}
                >
                  <motion.div
                    className="absolute right-0 top-0 bottom-0 w-1 bg-white/80 rounded-full"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guild Information - Clean styling */}
      {Object.keys(userData.guildRoles || {}).length > 0 && (
        <div className="bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20">
          <h3 className="text-xl font-bold text-white mb-6">Guild Memberships</h3>
          <div className="text-sm text-[#7C8694] mb-4">
            Active in {Object.keys(userData.guildRoles).length} guilds with {Object.values(userData.guildRoles || {}).flat().length} total roles
          </div>
          
          {userData.spawnGuildId && userData.spawnGuildId !== "Not set" && (
            <div className="p-4 bg-[#0A0E1B]/40 rounded-xl border border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-[#00FF9D]" />
                <span className="text-sm font-medium text-white">Origin Guild</span>
              </div>
              <div className="text-xs text-[#7C8694] font-mono">{userData.spawnGuildId}</div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default OverviewTab;